import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';

//components
import { Alert } from 'components/common/Alert';
import CheckboxController from 'controller/Checkbox';
import InputController from 'controller/InputController';

//contexts, interfaces, schemas
import {
  PRIVACY_POLICY_LINK,
  TERMS_AND_CONDITIONS_LINK,
  ALREADY_HAVE_AN_ACCOUNT_TEXT,
  AUTH_LINKS,
  EMAIL_ADDRESS_TEXT,
  FIRST_NAME_TEXT,
  HTTP_STATUS,
  LAST_NAME_TEXT,
  LOG_IN_BUTTON_TEXT,
  ORGANIZATION_NAME_TEXT,
  REGISTER_BTN_TEXT,
  signupUserInitialValues,
  VERIFICATION_MESSAGE_SENT_TEXT,
  PRIVACY_POLICY_TEXT,
  TERMS_AND_CONDITIONS_TEXT,
  AGREE_TO_TEXT,
  ACKNOWLEDGE_THE_TEXT,
} from 'constants/index';
import { useSignUpUserMutation } from 'generated/graphql';
import { SignupUserInput } from 'interfaces';
import { signupSchema } from 'validationSchema';
import { useRecaptchaV3 } from 'hooks';

export const SignupForm: React.FC = () => {
  const navigate = useNavigate();
  const { isReady, execute } = useRecaptchaV3();

  const methods = useForm<SignupUserInput>({
    resolver: yupResolver(signupSchema),
    defaultValues: signupUserInitialValues,
  });

  const { handleSubmit, reset } = methods;

  const [signup, { loading }] = useSignUpUserMutation({
    onCompleted: (data) => {
      if (data) {
        const { signUpUser } = data || {};
        const { response } = signUpUser || {};
        const { status, message } = response || {};

        if (status === HTTP_STATUS.CREATED) {
          Alert.success(message || VERIFICATION_MESSAGE_SENT_TEXT);
          reset();
          navigate(AUTH_LINKS.EMAIL_SENT_SUCCESS_LINK);
        } else {
          Alert.error(message || '');
        }
      }
    },
    onError: ({ message }) => {
      return Alert.error(message);
    },
  });

  const onSubmit: SubmitHandler<SignupUserInput> = async (data) => {
    const { email, firstName, lastName, organizationName } = data;
    const token = await execute({ action: 'signup' });
    await signup({
      variables: {
        userSignUpInput: {
          email: email.toLowerCase(),
          firstName,
          lastName,
          organizationName,
        },
      },
      context: {
        headers: {
          'x-recaptcha-token': token,
        },
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputController
              isRequired
              name={'organizationName'}
              title={ORGANIZATION_NAME_TEXT}
              fieldType={'text'}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputController
              isRequired
              name={'firstName'}
              title={FIRST_NAME_TEXT}
              fieldType={'text'}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <InputController
              isRequired
              name={'lastName'}
              title={LAST_NAME_TEXT}
              fieldType={'text'}
              disabled={loading}
            />
          </Grid>
          <Grid item xs={12}>
            <InputController
              isRequired
              name={'email'}
              title={EMAIL_ADDRESS_TEXT}
              fieldType={'email'}
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <CheckboxController
            name="termsAndConditions"
            isRequired
            disabled={loading}
            title={
              <Typography variant="subtitle2" color="white" mt={1}>
                {AGREE_TO_TEXT}{' '}
                <Typography
                  component={Link}
                  variant="subtitle2"
                  to={TERMS_AND_CONDITIONS_LINK}
                  color="primary"
                  target="_blank">
                  {TERMS_AND_CONDITIONS_TEXT}
                </Typography>{' '}
                {ACKNOWLEDGE_THE_TEXT}{' '}
                <Typography
                  component={Link}
                  variant="subtitle2"
                  to={PRIVACY_POLICY_LINK}
                  color="primary"
                  target="_blank">
                  {PRIVACY_POLICY_TEXT}
                </Typography>
                .
              </Typography>
            }
          />
        </Box>

        <Box marginTop={2} mb={4}>
          <Button
            variant="contained"
            type="submit"
            id="login-btn"
            fullWidth
            color="primary"
            disabled={loading || !isReady}
            endIcon={loading && <CircularProgress size={20} color="inherit" />}>
            {REGISTER_BTN_TEXT}
          </Button>
        </Box>

        <Box display="flex" alignItems="center" justifyContent="flex-end">
          <Typography variant="subtitle2" fontWeight={400} color="customGrayColor.light" mr={1}>
            {ALREADY_HAVE_AN_ACCOUNT_TEXT}
          </Typography>

          <Typography component={Link} variant="subtitle2" to={AUTH_LINKS.LOGIN_LINK} color="primary">
            {LOG_IN_BUTTON_TEXT}
          </Typography>
        </Box>
      </form>
    </FormProvider>
  );
};
